/* eslint-disable react/prop-types */
import React, {
  Component, Fragment, useEffect, useState,
} from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  Table, Button, Collapse, Alert,
  Spin,
  Typography,
} from 'antd';
import { LoadingOutlined, CaretRightOutlined, WarningFilled } from '@ant-design/icons';
import H3Text from '@Uilib/h3Text';
import './style.scss';
import BatchSelector from '@Components/Inventory/Indent/CreateIndent/BatchSelector';
import Helpers from '@Apis/helpers';
import STActions from '@Actions/stActions';
import DocConfigActions from '@Actions/docConfigActions';
import FormHelpers from '@Helpers/FormHelpers';
import TenantSelector from '@Components/Common/Selector/TenantSelector';
import dayjs from 'dayjs';

const { Text } = Typography;

const { Panel } = Collapse;

const BulkPreviewST = ({
  user, departments, prLines, callback, createStLoading, createSt, getDocConfig, docConfigStockTransfer,
}) => {
  const [previewSTData, setPreviewSTData] = useState([]);
  const [actionType, setActionType] = useState('');
  const [termsByTenant, setTermsByTenant] = useState({});

  useEffect(() => {
    const copyData = JSON.parse(JSON.stringify(prLines));

    const groupedMap = new Map();
    const tenantIdsSet = new Set();

    for (let i = 0; i < copyData?.length; i++) {
      const line = copyData?.[i];
      const {
        tenant_id,
        tenant_department_id,
        sourceTenantId,
        sourceTenantDepartmentId,
        department_info,
        sourceDepartmentName,
      } = line;
      // Add to tenant ID set
      if (sourceTenantId) tenantIdsSet.add(sourceTenantId);

      const groupKey = `${tenant_id}_${tenant_department_id}_${sourceTenantId}_${sourceTenantDepartmentId}`;

      if (!groupedMap.has(groupKey)) {
        groupedMap.set(groupKey, {
          tenant_id,
          tenant_name: user?.user_tenants?.find((tenant) => tenant?.tenant_id === tenant_id)?.tenant_name?.toUpperCase(),
          tenant_department_id,
          sourceTenantId,
          sourceTenantName: user?.user_tenants?.find((tenant) => tenant?.tenant_id === sourceTenantId)?.tenant_name?.toUpperCase(),
          sourceTenantDepartmentId,
          department_name: department_info?.department_name,
          sourceDepartmentName,
          pr_lines: [],
        });
      }

      groupedMap.get(groupKey)?.pr_lines?.push(line);
    }

    const groupedList = Array.from(groupedMap?.values());

    // Store to state for preview (if needed)
    setPreviewSTData(groupedList);

    const tenantIds = Array.from(tenantIdsSet).join(',');

    getDocConfig(tenantIds, 'STOCK_TRANSFER');
  }, []);

  useEffect(() => {
    const termsByTenantGroup = {};
    if (docConfigStockTransfer) {
      const configData = docConfigStockTransfer?.data?.find(item => item?.document_type === 'STOCK_TRANSFER_DOWNLOAD')?.configuration;

      (configData || []).forEach(cfg => {
        if (cfg?.tenant_id && cfg?.default_t_and_c !== undefined) {
          termsByTenantGroup[cfg.tenant_id] = cfg.default_t_and_c;
        }
      });
    }
    setTermsByTenant(termsByTenantGroup);
  }, [docConfigStockTransfer]);

  const prListColumns = [

    {
      title: 'PRODUCT',
      render: (record) => (
        <H3Text text={record?.product_sku_info?.product_sku_name} className="prod-table__text" />
      ),
      width: '40%',
    },
    {
      title: 'SKU CODE',
      render: (record) => <div>{`${record?.product_sku_info?.internal_sku_code}`}</div>,
      width: '10%',

    },
    {
      title: '#PR',
      render: (record) => <div>{`${record?.pr_number}`}</div>,
      width: '10%',

    },
    {
      title: 'QUANTITY',
      render: (record) => <div>{`${record?.quantity} ${record?.uom_info?.uqc?.toProperCase() || ''}`}</div>,
      width: '10%',

    },
    {
      title: 'SECONDARY QUANTITY',
      render: (record) => <div>{`${record?.secondary_uom_qty || '-'} ${record?.product_sku_info?.secondary_uom_info?.uqc?.toProperCase() || ''}`}</div>,
      width: '10%',

    },
  ];

  const handleCreateSt = (withApproval) => {
    const getStBatches = ({
      batches, sourceTenantProductId, destTenantProductId, sourceDepartment, destinationDepartment, data
    }) => {
      const updatedBatches = [];
      for (let i = 0; i < batches?.length; i++) {
        const newBatch = {
          ...batches[i],
          quantity: batches[i]?.consumed_qty,
          source_tenant_product_id: sourceTenantProductId,
          destination_tenant_product_id: destTenantProductId,
          source_tenant_department_id: sourceDepartment,
          destination_tenant_department_id: destinationDepartment,
          manufacturing_date: batches[i]?.manufacturing_date ? FormHelpers.dateFormatter(batches[i]?.manufacturing_date, data?.manufacturingDateFormat) : null,
          expiry_date: batches[i]?.expiry_date ? FormHelpers.dateFormatter(batches[i]?.expiry_date, data?.expiryDateFormat) : null,
        };
        delete newBatch?.tenant_department_id;
        delete newBatch?.tenant_product_id;
        if (Object.prototype.hasOwnProperty.call(newBatch, '_reserved')) {
          delete newBatch?._reserved;
        }
        updatedBatches.push(newBatch);
      }
      return updatedBatches;
    };

    const payload = [];

    previewSTData?.forEach((groupedData) => {
      const refPrIds = [];
      const stockTransferLines = [];
      groupedData?.pr_lines?.forEach((data) => {
        refPrIds.push(data?.purchase_requisition_id);
        stockTransferLines.push({
          pr_line_id: data?.pr_line_id,
          mo_line_id: data?.mo_line_id,
          tenant_product_id: data?.tenant_product_id,
          quantity: data?.quantity,
          secondary_uom_qty: data?.secondary_uom_qty,
          uom_id: data?.uom_info?.uom_id,
          uom_info: data?.uom_info,
          source_product_batches: getStBatches({
            batches: data?.product_batches?.filter((item) => item?.consumed_qty > 0),
            sourceTenantProductId: data?.tenant_product_id,
            destTenantProductId: null,
            sourceDepartment: data?.source_tenant_department_id,
            destinationDepartment: data?.tenant_department_id,
            data,
          }),
          // custom_fields: CustomFieldHelpers.postCfStructure(data[i]?.lineCustomFields),
        });
      });

      const tempPayload = {
        ref_pr_ids: [...new Set(refPrIds)].join(','),
        source_tenant_department_id: groupedData?.sourceTenantDepartmentId,
        destination_tenant_department_id: groupedData?.tenant_department_id,
        stock_transfer_date: dayjs().toISOString(),
        remark: '',
        status: withApproval ? 'ISSUED' : 'DRAFT',
        stock_transfer_lines: stockTransferLines,
        t_and_c: termsByTenant[groupedData?.sourceTenantId] || '',
      };

      payload.push(tempPayload);
    });

    createSt(payload, () => {
      setActionType('');
      callback();
    }, withApproval);
  };

  return (
    <div className="bulk-pr-verify-container">
      {!previewSTData?.length ? (
        <Spin tip="Loading">
          <div style={{
            padding: 50,
            background: 'rgba(0, 0, 0, 0.05)',
            borderRadius: 4,
          }}
          />
        </Spin>
      ) : (
        <React.Fragment>
          <div className="bulk-pr-verify-title">
            <Alert
              message={`${previewSTData?.length} Stock Transfers`}
              showIcon
              icon={<WarningFilled />}
              type="warning"
            />
          </div>
          {previewSTData?.map((groupItem, index) => (
            <Collapse
              key={`${groupItem?.tenant_id}-${groupItem?.tenant_department_id}-${index}`}
              bordered={false}
              defaultActiveKey={['1']}
              expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
            >
              <Panel
                header={
                  (
                    <div style={{ display: 'flex', flexDirection: 'column', color: '#2D7DF7', fontWeight: 600 }}>
                      <span>
                        Destination Location: {groupItem?.tenant_name} ({groupItem?.department_name})
                      </span>
                      <span>
                        Source Location: {groupItem?.sourceTenantName} ({groupItem?.sourceDepartmentName})
                      </span>
                      <span>
                        {groupItem?.pr_lines?.length || 0} Stock Transfer Lines
                      </span>
                    </div>
                  )}
                key={index}
              >
                <Table
                  key={`${groupItem?.sourceTenantId}-${groupItem?.sourceTenantDepartmentId}-${index}`}
                  bordered
                  showHeader
                  size="small"
                  tableLayout="fixed"
                  scroll="scroll"
                  columns={prListColumns}
                  dataSource={groupItem?.pr_lines}
                  pagination={false}
                  expandable={{
                    columnWidth: '2%',
                    rowExpandable: (record) =>
                      Helpers.getValueTotalInObject(record?.product_batches, 'available_qty') > 0,
                    expandedRowRender: (record) => (
                      <BatchSelector
                        batches={record?.product_batches}
                        uomInfo={record?.uom_info}
                        expiryDays={record?.expiry_days}
                        itemsRow={record}
                        productInfo={record}
                        noCalculations
                        productUomInfo={record?.product_sku_info?.uom_info?.[0]}
                        batchMethod={record?.batch_consumption_method}
                        allStock={record?.is_good_stock_only}
                        updateBatchConsumptionMethod={(value) => { }}
                        upDateAllStock={(value) => { }}
                        onToggleBatch={(record, itemsRow) => { }}
                        disableToggleBatch
                        disableBatchConsumptionMethod
                        disableAllBatchToggle
                        disableBatchConsumedQtyEditable
                      />
                    ),
                  }}
                />
              </Panel>
            </Collapse>
          ))}
          <div className="custom-drawer__footer" style={{ width: 'calc(100% - 60px)' }}>
            <Button
              type="primary"
              onClick={() => {
                setActionType('DRAFT');
                handleCreateSt(false);
              }}
              style={{ width: '150px' }}
              disabled={createStLoading}
            >
              {(createStLoading && actionType === 'DRAFT') ? <LoadingOutlined /> : 'Save as Draft'}
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setActionType('ISSUED');
                handleCreateSt(true);
              }}
              style={{ width: '150px', marginLeft: '10px' }}
              disabled={createStLoading}
            >
              {(createStLoading && actionType === 'ISSUED') ? <LoadingOutlined /> : 'Save and Issue'}
            </Button>
          </div>
        </React.Fragment>
      )}
    </div>
  );
};

const mapStateToProps = ({
  UserReducers, TenantReducers, DepartmentReducers, STReducers, DocConfigReducers,
}) => ({
  user: UserReducers.user,
  tenantsConfiguration: TenantReducers.tenantsConfiguration,
  departments: DepartmentReducers.departments,
  createStLoading: STReducers.createStLoading,
  docConfigStockTransfer: DocConfigReducers.docConfigStockTransfer,
});

const mapDispatchToProps = (dispatch) => ({
  createSt: (payload, callback, withApproval) => dispatch(STActions.createSt(payload, callback, withApproval)),
  getDocConfig: (tenantId, entityName, callback) => dispatch(DocConfigActions.getDocConfig(tenantId, entityName, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(BulkPreviewST));
